import DataTable, {Order} from "datatables.net-bs5";
import { Modal } from "bootstrap";
import * as Highcharts from "highcharts";
import 'highcharts/modules/drilldown';
import { getChartTheme } from './utils/chartTheme';
// v12: drilldown self-registers on side-effect import.
// 'animateDrilldown' on the pie prototype is the module-registration sentinel (D-01 loud guard).
if (typeof (Highcharts as any).seriesTypes?.['pie']?.prototype?.animateDrilldown === 'undefined') {
    console.error('[drilldown] module failed to register — pie drilldown will not work. Check the highcharts/modules/drilldown side-effect import.');
}
import {get, notifier, number_format, on} from "./util";
import { groupColor } from './constants';
import { makeEmptyAwareTooltip } from './utils/emptyAwareTooltip';
import { initImportModal } from './import-modal';

// Phase 181: the global `theme` string ternary pattern removed — chart chrome now via getChartTheme()
declare var txn_donut: any[];
declare var txn_monthly: any[];
declare var txn_monthly_by_group: Record<string, any[]>;
declare var txn_years: string[];
declare var txn_payload: Record<string, {
    by_group: { group: string; total: number }[];
    monthly_by_group: Record<string, { month: string; total: number; count: number }[]>;
    by_main: { main_id: number; main_name: string; main_color: string; total: number }[];
    monthly_by_main: Record<string, { month: string; total: number; count: number }[]>;
    by_sub_top10: { sub_id: number; sub_name: string; main_id: number; main_color: string; total: number }[];
    monthly_by_sub_top10: Record<string, { month: string; total: number; count: number }[]>;
    stats: any;
}>;
declare var txn_current_year: string;

// Phase 156: merchant deep-dive page-injected vars (set by merchant.latte page_js block)
declare var merchant_trend: { month: string; total: number; count: number }[];
declare var merchant_breakdown: { main_id: number; main_name: string; main_color: string; sub_id: number; sub_name: string; total: number }[];
declare var merchant_stats: any;
declare var merchant_key: string;

export default class {
    static init = () => {
        list();
        initImportModal({
            onSuccess: (_result) => {
                if (datatable) datatable.ajax.reload();
            }
        });
        undoButtons();
        stats();
        review();
        initRecipients();
        initRecurring();
        initMerchant();
        amazonAutoMatches();
        paypalDataTables();
        splitCategoryModal();
        txnCategoryModal();
        initSplitBuilderModal();
    }
}

// Phase 157 — client-side DataTable for the Amazon "Recent Auto-Matches" section so the
// operator can page/search/sort the full match history (back to 2020), not just the latest 50.
// Guarded to the amazon page by the data-table presence check. Unsplit reloads the page on
// success (no DOM row removal), so the DataTable never desyncs. The table inits while its
// accordion is collapsed (hidden) → autoWidth:false + columns.adjust() on show avoids header drift.
function amazonAutoMatches() {
    const table = document.querySelector('.datatable[data-table="amazon_auto_matches"]') as HTMLElement | null;
    if (!table) return;
    const dt = new DataTable(table, {
        pageLength: 25,
        lengthMenu: [10, 25, 50, 100],
        order: [[0, 'desc']] as unknown as Order,
        autoWidth: false,
        columnDefs: [
            { targets: 2, orderable: false },                    // Items (bullet breakdown) — searchable, not sortable
            { targets: 3, orderable: false, searchable: false }, // Action (Unsplit)
        ],
    });
    const section = document.getElementById('amz-sec-matches');
    if (section) {
        section.addEventListener('shown.bs.collapse', () => dt.columns.adjust());
    }
}

// Phase 160 — client-side DataTables for the PayPal de-obfuscation page.
// Two tables: paypal_auto_matches (Recent Auto-Matches sub-accordion) and
// paypal_transactions (all PayPal rows, Region B — server-rendered synchronously, NOT deferred).
// Page-guarded: returns early if neither table is present (this module loads on every
// Transactions page — guard prevents double-bind and errors on non-PayPal pages).
// Both tables init while collapsed → autoWidth:false + columns.adjust() on show avoids header drift.
// Default order: Date descending (column 0, 'desc') for both tables.
// No server-side ajax source — Region B is synchronously server-rendered (UI-SPEC :482-488).
function paypalDataTables() {
    const matchesTable  = document.querySelector('.datatable[data-table="paypal_auto_matches"]') as HTMLElement | null;
    const allTxnTable   = document.querySelector('.datatable[data-table="paypal_transactions"]') as HTMLElement | null;
    if (!matchesTable && !allTxnTable) return;  // guard: paypal page only

    // Recent Auto-Matches table (sub-accordion inside Region A)
    if (matchesTable) {
        const dtMatches = new DataTable(matchesTable, {
            pageLength: 25,
            lengthMenu: [10, 25, 50, 100],
            order: [[0, 'desc']] as unknown as Order,
            autoWidth: false,
            columnDefs: [
                { targets: 2, orderable: false, searchable: false }, // Action (Unsplit)
            ],
        });
        const matchesSection = document.getElementById('pp-sec-matches');
        if (matchesSection) {
            matchesSection.addEventListener('shown.bs.collapse', () => dtMatches.columns.adjust());
        }
    }

    // All PayPal Transactions table (Region B — client-side paged, 1,274 rows manageable)
    if (allTxnTable) {
        const dtAll = new DataTable(allTxnTable, {
            pageLength: 25,
            lengthMenu: [10, 25, 50, 100],
            order: [[0, 'desc']] as unknown as Order,
            autoWidth: false,
            columnDefs: [
                { targets: 5, orderable: false, searchable: false }, // Category (no meaningful sort)
            ],
        });
        const allSection = document.getElementById('pp-sec-all');
        if (allSection) {
            allSection.addEventListener('shown.bs.collapse', () => dtAll.columns.adjust());
        }
    }
}

// Phase 157 UAT — inline recategorize modal for Amazon split items on the /transactions list.
// Each split item's category cell is a .js-split-cat-edit trigger; clicking opens a small modal
// with a grouped category/sub-cat <select> (built from the injected categoryTree), pre-selected to
// the item's current sub-category. Save POSTs to handleSetSplitCategory and reloads so the
// split-detail cell AND the v_effective_amounts-based home/stats aggregates reflect the change.
function splitCategoryModal() {
    const modalEl = document.getElementById('splitCatModal');
    if (!modalEl) return; // list page only

    const categoryTree: { id: number; name: string; subs: { id: number; name: string }[] }[] =
        (window as any).categoryTree || [];
    const saveUrl = modalEl.getAttribute('data-save-url') || '';
    // Use the imported Modal — this app never assigns bootstrap to window, so
    // (window as any).bootstrap is undefined (the home modals use the import too).
    const modal = Modal.getOrCreateInstance(modalEl);

    const selectContainer = modalEl.querySelector('.js-split-cat-select-container') as HTMLElement | null;
    const itemLabel       = modalEl.querySelector('.js-split-cat-item') as HTMLElement | null;
    const errEl           = modalEl.querySelector('.js-split-cat-error') as HTMLElement | null;
    const saveBtn         = modalEl.querySelector('.js-split-cat-save') as HTMLButtonElement | null;

    let currentSplitId = '';
    let currentBtn: HTMLElement | null = null;

    function buildSelect(currentSubCatId: number): HTMLSelectElement {
        const sel = document.createElement('select');
        sel.className = 'form-select form-select-sm js-split-cat-select';
        categoryTree.forEach(main => {
            const og = document.createElement('optgroup');
            og.label = main.name;
            main.subs.forEach(sub => {
                const opt = document.createElement('option');
                opt.value = String(sub.id);
                opt.textContent = sub.name;
                if (sub.id === currentSubCatId) opt.selected = true;
                og.appendChild(opt);
            });
            sel.appendChild(og);
        });
        return sel;
    }

    // Delegated — split child rows render dynamically on expand.
    document.addEventListener('click', (e: Event) => {
        const btn = (e.target as HTMLElement).closest('.js-split-cat-edit') as HTMLElement | null;
        if (!btn) return;
        currentSplitId = btn.dataset.splitId || '';
        currentBtn = btn;
        const subCatId = parseInt(btn.dataset.subCatId || '0', 10);
        const desc = (btn.closest('tr')?.querySelector('td')?.textContent || '').trim();
        if (itemLabel) itemLabel.textContent = desc;
        if (selectContainer) { selectContainer.innerHTML = ''; selectContainer.appendChild(buildSelect(subCatId)); }
        if (errEl) errEl.classList.add('d-none');
        if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = 'Save'; }
        modal?.show();
    });

    if (saveBtn) {
        saveBtn.addEventListener('click', () => {
            const sel = modalEl.querySelector('.js-split-cat-select') as HTMLSelectElement | null;
            const newSubCatId = sel?.value || '';
            if (!currentSplitId || !newSubCatId) return;
            saveBtn.disabled = true;
            saveBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';
            if (errEl) errEl.classList.add('d-none');
            fetch(saveUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: 'splitId=' + encodeURIComponent(currentSplitId) + '&subCatId=' + encodeURIComponent(newSubCatId),
            })
            .then(r => r.json())
            .then((data: any) => {
                if (data.success) {
                    const newName = sel?.selectedOptions[0]?.textContent || '';
                    const newId   = parseInt(newSubCatId, 10);
                    // 1) Update the clicked cell's badge + data attr in place (no reload).
                    if (currentBtn) {
                        const badge = currentBtn.querySelector('.badge');
                        if (badge) badge.textContent = newName;
                        currentBtn.dataset.subCatId = newSubCatId;
                    }
                    // 2) Patch the cached DataTable row data so collapse/re-expand stays
                    //    consistent — no ajax.reload(), no full redraw.
                    if (datatable && currentSplitId) {
                        datatable.rows().every(function (this: any) {
                            const d = this.data();
                            if (d && Array.isArray(d.splits)) {
                                const sp = d.splits.find((s: any) => String(s.id) === currentSplitId);
                                if (sp) { sp.sub_cat_id = newId; sp.sub_cat_name = newName; }
                            }
                        });
                    }
                    modal.hide();
                    saveBtn.disabled = false;
                    saveBtn.textContent = 'Save';
                } else {
                    saveBtn.disabled = false;
                    saveBtn.textContent = 'Save';
                    if (errEl) { errEl.textContent = data.message || 'Could not update.'; errEl.classList.remove('d-none'); }
                }
            })
            .catch(() => {
                saveBtn.disabled = false;
                saveBtn.textContent = 'Save';
                if (errEl) { errEl.textContent = 'Network error. Try again.'; errEl.classList.remove('d-none'); }
            });
        });
    }
}

// 158 UAT — recategorize a top-level transaction from the /transactions list Category column.
// Click a .js-txn-cat-edit cell → modal with the grouped categoryTree <select> pre-selected to
// the row's sub_cat_id → Save POSTs txnId + subCatId to handleSetTransactionCategory, then reloads.
// Mirrors splitCategoryModal() (split items); split PARENTS aren't clickable (server also refuses).
function txnCategoryModal() {
    const modalEl = document.getElementById('txnCatModal');
    if (!modalEl) return; // list page only

    const categoryTree: { id: number; name: string; subs: { id: number; name: string }[] }[] =
        (window as any).categoryTree || [];
    const saveUrl = modalEl.getAttribute('data-save-url') || '';
    const modal = Modal.getOrCreateInstance(modalEl);

    const selectContainer = modalEl.querySelector('.js-txn-cat-select-container') as HTMLElement | null;
    const itemLabel       = modalEl.querySelector('.js-txn-cat-item') as HTMLElement | null;
    const errEl           = modalEl.querySelector('.js-txn-cat-error') as HTMLElement | null;
    const saveBtn         = modalEl.querySelector('.js-txn-cat-save') as HTMLButtonElement | null;

    let currentTxnId = '';

    function buildSelect(currentSubCatId: number): HTMLSelectElement {
        const sel = document.createElement('select');
        sel.className = 'form-select form-select-sm js-txn-cat-select';
        categoryTree.forEach(main => {
            const og = document.createElement('optgroup');
            og.label = main.name;
            main.subs.forEach(sub => {
                const opt = document.createElement('option');
                opt.value = String(sub.id);
                opt.textContent = sub.name;
                if (sub.id === currentSubCatId) opt.selected = true;
                og.appendChild(opt);
            });
            sel.appendChild(og);
        });
        return sel;
    }

    document.addEventListener('click', (e: Event) => {
        const link = (e.target as HTMLElement).closest('.js-txn-cat-edit') as HTMLElement | null;
        if (!link) return;
        e.preventDefault();
        currentTxnId = link.dataset.txnId || '';
        const subCatId = parseInt(link.dataset.subCatId || '0', 10);
        // Resolve the row's name from the DataTable for the modal label.
        const tr = link.closest('tr') as HTMLTableRowElement | null;
        const rowData: any = (tr && datatable) ? datatable.row(tr).data() : null;
        const desc = rowData ? ((rowData.name ?? '') + ' — $' + number_format(parseFloat(rowData.amount ?? '0'), 2)) : '';
        if (itemLabel) itemLabel.textContent = desc;
        if (selectContainer) { selectContainer.innerHTML = ''; selectContainer.appendChild(buildSelect(subCatId)); }
        if (errEl) errEl.classList.add('d-none');
        if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = 'Save'; }
        modal?.show();
    });

    if (saveBtn) {
        saveBtn.addEventListener('click', () => {
            const sel = modalEl.querySelector('.js-txn-cat-select') as HTMLSelectElement | null;
            const newSubCatId = sel?.value || '';
            if (!currentTxnId || !newSubCatId) return;
            saveBtn.disabled = true;
            saveBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';
            if (errEl) errEl.classList.add('d-none');
            fetch(saveUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: 'txnId=' + encodeURIComponent(currentTxnId) + '&subCatId=' + encodeURIComponent(newSubCatId),
            })
            .then(r => r.json())
            .then((data: any) => {
                if (data.success) {
                    modal.hide();
                    saveBtn.disabled = false;
                    saveBtn.textContent = 'Save';
                    if (datatable) datatable.ajax.reload(null, false); // keep current page
                } else {
                    saveBtn.disabled = false;
                    saveBtn.textContent = 'Save';
                    if (errEl) { errEl.textContent = data.message || 'Could not update.'; errEl.classList.remove('d-none'); }
                }
            })
            .catch(() => {
                saveBtn.disabled = false;
                saveBtn.textContent = 'Save';
                if (errEl) { errEl.textContent = 'Network error. Try again.'; errEl.classList.remove('d-none'); }
            });
        });
    }
}

// Phase 158 D-01..D-07/D-10 — Manual split builder modal.
// Opens the #splitTxnModal for new splits (pre-filled with full amount + parent category)
// or edit mode (pre-loaded with existing child lines). Each line row has:
//   - a number input (.js-split-amount)
//   - a grouped category <select> built via buildSelect() (D-03 — reuses Phase 157 function)
//   - a "Fill remaining" button (.js-fill-remaining) (D-07)
//   - a remove-line "×" button (hidden when only one line remains)
// Live remaining indicator updates on every input event (integer-cents math — no float ==).
// Save POSTs txnId + lines JSON to data-save-url (new split) or data-edit-url (edit).
// Do NOT add a categoryTree injection here — already present in list.latte (no duplicate).
function splitBuilderModal(txnId: number, txnAmount: number, txnDesc: string, currentSubCatId: number, existingLines?: { amount: number; sub_cat_id: number }[]): void {
    const modalEl = document.getElementById('splitTxnModal');
    if (!modalEl) return;

    const categoryTree: { id: number; name: string; subs: { id: number; name: string }[] }[] =
        (window as any).categoryTree || [];

    const saveUrl     = modalEl.getAttribute('data-save-url') || '';
    const editUrl     = modalEl.getAttribute('data-edit-url') || '';
    const isEdit      = !!(existingLines && existingLines.length > 0);
    const useUrl      = isEdit ? editUrl : saveUrl;

    // Use the imported Modal — not the window global (undefined in this project)
    const modal = Modal.getOrCreateInstance(modalEl);

    const titleEl       = modalEl.querySelector('.js-split-modal-title') as HTMLElement | null;
    const descEl        = modalEl.querySelector('.js-split-modal-desc') as HTMLElement | null;
    const linesContainer = modalEl.querySelector('#js-split-lines-container') as HTMLElement | null;
    const addLineBtn    = modalEl.querySelector('.js-add-split-line') as HTMLButtonElement | null;
    const remainingEl   = modalEl.querySelector('.js-remaining-indicator') as HTMLElement | null;
    const errorEl       = modalEl.querySelector('.js-split-modal-error') as HTMLElement | null;
    const saveBtn       = modalEl.querySelector('.js-split-save') as HTMLButtonElement | null;

    if (!linesContainer || !saveBtn) return;

    // Total in integer cents to avoid float drift (D-06 exact-to-the-penny)
    const totalCents = Math.round(txnAmount * 100);

    // Reset modal state
    if (titleEl) titleEl.textContent = isEdit ? 'Edit split' : 'Split transaction';
    if (descEl) descEl.textContent = txnDesc;
    if (errorEl) { errorEl.classList.add('d-none'); errorEl.textContent = ''; }
    if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = 'Save split'; }
    linesContainer.innerHTML = '';

    // Build a category <select> using the injected categoryTree (D-03 — same grouped main→sub select)
    function buildSplitSelect(subCatId: number): HTMLSelectElement {
        const sel = document.createElement('select');
        sel.className = 'form-select form-select-sm';
        // Default empty option
        const empty = document.createElement('option');
        empty.value = '';
        empty.textContent = '— Select category —';
        sel.appendChild(empty);
        categoryTree.forEach(main => {
            const og = document.createElement('optgroup');
            og.label = main.name;
            main.subs.forEach(sub => {
                const opt = document.createElement('option');
                opt.value = String(sub.id);
                opt.textContent = sub.name;
                if (sub.id === subCatId) opt.selected = true;
                og.appendChild(opt);
            });
            sel.appendChild(og);
        });
        return sel;
    }

    // Compute allocated cents from all .js-split-amount inputs
    function getAllocatedCents(): number {
        const inputs = linesContainer.querySelectorAll('.js-split-amount') as NodeListOf<HTMLInputElement>;
        let sum = 0;
        inputs.forEach(inp => {
            const v = parseFloat(inp.value);
            if (!isNaN(v) && v > 0) sum += Math.round(v * 100);
        });
        return sum;
    }

    // Update the remaining indicator and Save button state
    function updateRemaining(): void {
        const allocated = getAllocatedCents();
        const remaining = totalCents - allocated;
        if (!remainingEl) return;
        if (remaining === 0) {
            remainingEl.innerHTML = '<span class="text-success">Fully allocated — $0.00 remaining</span>';
        } else if (remaining > 0) {
            remainingEl.innerHTML = `<span class="text-muted">$${(remaining / 100).toFixed(2)} remaining</span>`;
        } else {
            remainingEl.innerHTML = `<span class="text-danger">Over-allocated by $${(Math.abs(remaining) / 100).toFixed(2)}</span>`;
        }
        // Disable Save when over-allocated
        if (saveBtn) saveBtn.disabled = remaining < 0;
        // Update Fill remaining button states — disable when remaining <= 0
        linesContainer.querySelectorAll('.js-fill-remaining').forEach((btn) => {
            (btn as HTMLButtonElement).disabled = remaining <= 0;
        });
    }

    // Create a split line row DOM element
    function createLineRow(amount: number | null, subCatId: number, index: number): HTMLElement {
        const row = document.createElement('div');
        row.className = 'split-line-row d-flex gap-2 mb-2 align-items-center';
        row.dataset.lineIndex = String(index);

        const amtInput = document.createElement('input');
        amtInput.type = 'number';
        amtInput.min = '0.01';
        amtInput.step = '0.01';
        amtInput.className = 'form-control form-control-sm js-split-amount';
        amtInput.style.maxWidth = '110px';
        if (amount !== null && amount > 0) amtInput.value = amount.toFixed(2);
        amtInput.placeholder = '0.00';
        amtInput.addEventListener('input', updateRemaining);

        const catContainer = document.createElement('div');
        catContainer.className = 'flex-grow-1';
        catContainer.appendChild(buildSplitSelect(subCatId));

        const fillBtn = document.createElement('button');
        fillBtn.type = 'button';
        fillBtn.className = 'btn btn-outline-secondary btn-sm js-fill-remaining';
        fillBtn.style.minHeight = '34px';
        fillBtn.textContent = 'Fill remaining';
        fillBtn.addEventListener('click', () => {
            const allocated = getAllocatedCents();
            // Exclude this row's current value from calculation
            const thisVal = parseFloat(amtInput.value);
            const thisValCents = !isNaN(thisVal) && thisVal > 0 ? Math.round(thisVal * 100) : 0;
            const remaining = totalCents - (allocated - thisValCents);
            if (remaining > 0) {
                amtInput.value = (remaining / 100).toFixed(2);
                updateRemaining();
            }
        });

        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'btn btn-outline-danger btn-sm js-remove-line';
        removeBtn.title = 'Remove line';
        removeBtn.textContent = '×';
        removeBtn.addEventListener('click', () => {
            const rows = linesContainer.querySelectorAll('.split-line-row');
            if (rows.length <= 1) return; // can't remove last row
            row.remove();
            // Re-index remaining rows
            linesContainer.querySelectorAll('.split-line-row').forEach((r, i) => {
                (r as HTMLElement).dataset.lineIndex = String(i);
            });
            updateRemoveButtons();
            updateRemaining();
        });

        row.appendChild(amtInput);
        row.appendChild(catContainer);
        row.appendChild(fillBtn);
        row.appendChild(removeBtn);
        return row;
    }

    // Show/hide remove buttons: hide when only one row remains
    function updateRemoveButtons(): void {
        const rows = linesContainer.querySelectorAll('.split-line-row');
        rows.forEach(row => {
            const btn = row.querySelector('.js-remove-line') as HTMLElement | null;
            if (btn) btn.style.display = rows.length <= 1 ? 'none' : '';
        });
    }

    // Populate initial lines
    if (isEdit && existingLines) {
        existingLines.forEach((line, i) => createLineRow(line.amount, line.sub_cat_id, i)).forEach(r => linesContainer.appendChild(r));
    } else {
        // New split: pre-fill with full amount + parent's current sub-category (D-04)
        linesContainer.appendChild(createLineRow(txnAmount, currentSubCatId, 0));
    }
    updateRemoveButtons();
    updateRemaining();

    // Add line button
    if (addLineBtn) {
        // Remove old listener by cloning
        const newAddBtn = addLineBtn.cloneNode(true) as HTMLButtonElement;
        addLineBtn.parentNode?.replaceChild(newAddBtn, addLineBtn);
        newAddBtn.addEventListener('click', () => {
            const count = linesContainer.querySelectorAll('.split-line-row').length;
            linesContainer.appendChild(createLineRow(null, 0, count));
            updateRemoveButtons();
            updateRemaining();
            // Focus new amount input
            const newRow = linesContainer.querySelector('.split-line-row:last-child .js-split-amount') as HTMLInputElement | null;
            newRow?.focus();
        });
    }

    // Wire save button (clone to remove stale listeners)
    const newSaveBtn = saveBtn.cloneNode(true) as HTMLButtonElement;
    saveBtn.parentNode?.replaceChild(newSaveBtn, saveBtn);
    const activeSaveBtn = modalEl.querySelector('.js-split-save') as HTMLButtonElement;

    activeSaveBtn.addEventListener('click', () => {
        if (!errorEl) return;
        errorEl.classList.add('d-none');
        errorEl.textContent = '';

        // Validate all lines
        const rows = linesContainer.querySelectorAll('.split-line-row');
        const lines: { amount: number; subCatId: number }[] = [];
        let valid = true;

        rows.forEach(row => {
            const amtInput = row.querySelector('.js-split-amount') as HTMLInputElement | null;
            const catSel   = row.querySelector('select') as HTMLSelectElement | null;
            const amt = parseFloat(amtInput?.value || '');
            const subCatId = parseInt(catSel?.value || '0', 10);

            if (!amtInput?.value || isNaN(amt)) {
                errorEl.textContent = 'Enter an amount for each line.';
                valid = false;
            } else if (amt <= 0) {
                errorEl.textContent = 'Amount must be greater than $0.00.';
                valid = false;
            } else if (!subCatId) {
                errorEl.textContent = 'Select a category for each line.';
                valid = false;
            } else {
                lines.push({ amount: amt, subCatId });
            }
        });

        if (!valid) {
            errorEl.classList.remove('d-none');
            return;
        }

        // Check over-allocation
        const allocated = lines.reduce((s, l) => s + Math.round(l.amount * 100), 0);
        const remaining = totalCents - allocated;
        if (remaining < 0) {
            errorEl.textContent = `Lines exceed the transaction total by $${(Math.abs(remaining) / 100).toFixed(2)}. Reduce amounts or remove a line.`;
            errorEl.classList.remove('d-none');
            return;
        }

        activeSaveBtn.disabled = true;
        activeSaveBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';

        const body = new URLSearchParams();
        body.set('txnId', String(txnId));
        body.set('lines', JSON.stringify(lines.map(l => ({ amount: l.amount, subCatId: l.subCatId }))));

        fetch(useUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        })
        .then(r => r.json())
        .then((data: any) => {
            if (data.success) {
                modal.hide();
                if (datatable) datatable.ajax.reload();
            } else {
                activeSaveBtn.disabled = false;
                activeSaveBtn.textContent = 'Save split';
                if (errorEl) {
                    errorEl.textContent = data.message || 'Could not save split. Please try again.';
                    errorEl.classList.remove('d-none');
                }
            }
        })
        .catch(() => {
            activeSaveBtn.disabled = false;
            activeSaveBtn.textContent = 'Save split';
            if (errorEl) {
                errorEl.textContent = 'Could not save split. Please try again.';
                errorEl.classList.remove('d-none');
            }
        });
    });

    modal.show();
}

// Initialize event bindings for the split builder modal (delegated, list page only).
function initSplitBuilderModal(): void {
    const modalEl = document.getElementById('splitTxnModal');
    if (!modalEl) return; // list page only guard

    // Delegated handler for .js-split-open (new split) — D-01/D-04
    document.addEventListener('click', (e: Event) => {
        const btn = (e.target as HTMLElement).closest('.js-split-open') as HTMLElement | null;
        if (!btn) return;
        const txnId      = parseInt(btn.dataset.txnId || '0', 10);
        const txnAmount  = parseFloat(btn.dataset.txnAmount || '0');
        const txnDesc    = btn.dataset.txnDesc || '';
        const subCatId   = parseInt(btn.dataset.subCatId || '0', 10);
        if (!txnId) return;
        splitBuilderModal(txnId, txnAmount, txnDesc, subCatId);
    });

    // Delegated handler for .js-edit-split (edit existing manual split) — D-10
    document.addEventListener('click', (e: Event) => {
        const btn = (e.target as HTMLElement).closest('.js-edit-split') as HTMLElement | null;
        if (!btn) return;
        const txnId    = parseInt(btn.dataset.txnId || '0', 10);
        const txnAmount = parseFloat(btn.dataset.txnAmount || '0');
        const txnDesc  = btn.dataset.txnDesc || '';
        const subCatId = parseInt(btn.dataset.subCatId || '0', 10);
        if (!txnId) return;
        // Existing lines are embedded in the DataTable row data
        const row = datatable?.row(btn.closest('tr') as HTMLElement);
        const rowData = row?.data?.();
        const existingLines: { amount: number; sub_cat_id: number }[] = (rowData?.splits || []).map((s: any) => ({
            amount: parseFloat(s.amount),
            sub_cat_id: parseInt(s.sub_cat_id, 10),
        }));
        splitBuilderModal(txnId, txnAmount, txnDesc, subCatId, existingLines);
    });

    // Delegated handler for .js-unsplit — D-09/D-11 (reuses existing handleUnsplit signal)
    document.addEventListener('click', (e: Event) => {
        const btn = (e.target as HTMLElement).closest('.js-unsplit') as HTMLElement | null;
        if (!btn) return;
        const txnId = btn.getAttribute('data-txn-id');
        if (!txnId) return;

        // 158 UAT: confirm before unsplitting — it deletes the split line items (destructive).
        if (!confirm('Unsplit this transaction? Its split line items will be removed and it returns to a single charge.')) {
            return;
        }

        btn.setAttribute('disabled', 'disabled');
        const origText = btn.textContent || 'Unsplit';
        btn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';

        const body = new URLSearchParams();
        body.set('txnId', txnId);

        fetch('?do=unsplit', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        })
        .then(r => r.json())
        .then((data: any) => {
            if (data.success) {
                if (datatable) datatable.ajax.reload();
            } else {
                btn.removeAttribute('disabled');
                btn.textContent = origText;
                // Show error near button
                const td = btn.closest('td');
                if (td) {
                    const errSpan = document.createElement('span');
                    errSpan.className = 'text-danger small ms-1';
                    errSpan.textContent = data.message || 'Could not unsplit. Please try again.';
                    td.appendChild(errSpan);
                    setTimeout(() => errSpan.remove(), 4000);
                }
            }
        })
        .catch(() => {
            btn.removeAttribute('disabled');
            btn.textContent = origText;
        });
    });
}

function abbreviateCurrency(amount: number): string {
    if (amount >= 1_000_000) return '$' + (amount / 1_000_000).toFixed(1) + 'M';
    if (amount >= 1_000) return '$' + (amount / 1_000).toFixed(1) + 'K';
    return '$' + amount.toFixed(2);
}

function getChartById(id: string): Highcharts.Chart | undefined {
    return (Highcharts.charts as (Highcharts.Chart | undefined)[])
        .find((c: any) => c?.renderTo?.id === id);
}

let barLevel: 'main' | 'sub' = 'main';
let currentYear: string = '';   // set in stats() from txn_current_year

// Sub-color shade helper (D-08): evenly spaced brighten steps from +0.3 (lightest) to -0.3 (darkest).
// Uses (Highcharts as any).color() — TS types don't expose Highcharts.color() directly.
function subColor(parentHex: string, index: number, total: number): string {
    const maxBrighten = 0.3;
    const step = total <= 1 ? 0 : maxBrighten - (index / (total - 1)) * maxBrighten * 2;
    return (Highcharts as any).color(parentHex).brighten(step).get();
}

// Build the drilldown sub-series array for the donut (D-01/D-02).
// Groups by_sub_top10 entries by main_id; skips the 'Other' bucket (main_id=0, non-interactive D-05).
function buildDonutDrillSeries(yearData: any): any[] {
    const subs: any[] = yearData.by_sub_top10 || [];
    const byMain: Record<number, any[]> = {};
    for (const sub of subs) {
        if (sub.main_id === 0) continue;  // skip 'Other' bucket — non-interactive (D-05)
        if (!byMain[sub.main_id]) byMain[sub.main_id] = [];
        byMain[sub.main_id].push(sub);
    }
    return Object.entries(byMain).map(([mainId, mainSubs]) => {
        const total = mainSubs.length;
        return {
            id: mainId,              // matches the main-level point's drilldown: String(main_id)
            type: 'pie' as const,
            name: mainSubs[0]?.main_name ?? '',  // used in breadcrumb
            data: mainSubs.map((s, i) => ({
                name: s.sub_name,
                y: s.total,
                color: subColor(s.main_color, i, total),
            })),
        };
    });
}

function buildMonthlySeries(yearData: any): any[] {
    const byGroup = yearData.monthly_by_group || {};
    const groupTotals: [string, number][] = Object.entries(byGroup).map(
        ([g, rows]) => [g, (rows as any[]).reduce((s, r) => s + (r.total as number), 0)]
    );
    // Sort ascending by total (smallest first = drawn ON TOP under reversedStacks:true);
    // tie-break ascending group name (deterministic).
    groupTotals.sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0]));
    const nonZero = groupTotals.filter(([, t]) => t > 0);
    return nonZero.map(([groupName]) => ({
        type: 'column' as const,
        name: groupName,
        color: groupColor(groupName),
        data: (byGroup[groupName] as any[]).map(r => r.total),
    }));
}

function getMonthCategories(yearData: any, year: string): string[] {
    const groups = Object.values(yearData.monthly_by_group || {}) as any[][];
    if (groups.length > 0) return groups[0].map((r: any) => r.month);
    // Empty-year fallback: 12 months YYYY-MM (RESEARCH Pitfall 4)
    if (year === 'All Years') return [];
    return Array.from({ length: 12 }, (_, i) =>
        year + '-' + String(i + 1).padStart(2, '0')
    );
}

// Build main-level monthly series (D-04/D-06): reads monthly_by_main, colors from by_main.main_color.
// Follows the same ascending-sort + nonZero discipline as buildMonthlySeries.
function buildMainSeries(yearData: any): any[] {
    const byMain = yearData.monthly_by_main || {};
    const mainMeta: any[] = yearData.by_main || [];
    const mainTotals: [string, number][] = Object.entries(byMain).map(
        ([name, rows]) => [name, (rows as any[]).reduce((s, r) => s + (r.total as number), 0)]
    );
    mainTotals.sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0]));
    const nonZero = mainTotals.filter(([, t]) => t > 0);
    return nonZero.map(([mainName]) => {
        const meta = mainMeta.find((m: any) => m.main_name === mainName);
        const color = meta?.main_color ? meta.main_color : groupColor(mainName);
        return {
            type: 'column' as const,
            name: mainName,
            color,
            data: (byMain[mainName] as any[]).map(r => r.total),
        };
    });
}

// Build sub-level monthly series (D-04/D-06): reads monthly_by_sub_top10, colors via subColor().
// 'Other' bucket falls back to groupColor('Other') / '#B2BEC3' (no parent main_color).
function buildSubSeries(yearData: any): any[] {
    const bySubMonthly = yearData.monthly_by_sub_top10 || {};
    const subMeta: any[] = yearData.by_sub_top10 || [];
    const subTotals: [string, number][] = Object.entries(bySubMonthly).map(
        ([name, rows]) => [name, (rows as any[]).reduce((s, r) => s + (r.total as number), 0)]
    );
    subTotals.sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0]));
    const nonZero = subTotals.filter(([, t]) => t > 0);
    const totalCount = nonZero.length;
    return nonZero.map(([subName], idx) => {
        const meta = subMeta.find((s: any) => s.sub_name === subName);
        let color: string;
        if (meta && meta.main_color && subName !== 'Other') {
            color = subColor(meta.main_color, idx, totalCount);
        } else {
            color = groupColor('Other') || '#B2BEC3';
        }
        return {
            type: 'column' as const,
            name: subName,
            color,
            data: (bySubMonthly[subName] as any[]).map(r => r.total),
        };
    });
}

// Level-aware bar re-render (D-04/D-06): remove all series, add new ones per barLevel, single redraw.
// Follows the remove-add-redraw discipline: remove all → add all → single redraw at the end.
function renderBarForLevel(barChart: Highcharts.Chart, yearData: any, year: string): void {
    while (barChart.series.length > 0) barChart.series[0].remove(false);
    const newSeries = barLevel === 'main' ? buildMainSeries(yearData) : buildSubSeries(yearData);
    newSeries.forEach(s => barChart.addSeries(s, false));
    // Use monthly_by_main or monthly_by_sub_top10 for x-axis categories
    const monthSource = barLevel === 'main'
        ? (Object.values(yearData.monthly_by_main || {}) as any[][])
        : (Object.values(yearData.monthly_by_sub_top10 || {}) as any[][]);
    const categories: string[] = monthSource.length > 0
        ? monthSource[0].map((r: any) => r.month)
        : getMonthCategories(yearData, year);
    barChart.xAxis[0].setCategories(categories, false);
    barChart.setTitle(
        { text: barLevel === 'main' ? 'Monthly Spending by Category' : 'Monthly Spending by Sub-Category' },
        undefined,
        false
    );
    barChart.redraw();
}


function updateStatCards(year: string, stats: any): void {
    const isAllYears = year === 'All Years';
    const isCurrentYear = year === String(new Date().getFullYear());

    const setText = (id: string, text: string) => {
        const el = document.getElementById(id);
        if (el) el.textContent = text;
    };
    const setHTML = (id: string, html: string) => {
        const el = document.getElementById(id);
        if (el) el.innerHTML = html;
    };
    const badge = (change: number | null) => {
        if (change === null || change === undefined) return '<span class="text-muted small">N/A</span>';
        const cls = change >= 0 ? 'bg-danger' : 'bg-success';
        const arrow = change >= 0 ? '&#8593;' : '&#8595;';
        return `<span class="badge ${cls}">${arrow} ${Math.abs(change)}%</span>`;
    };

    // Card 1: Total
    setText('js-stat-label-total', isAllYears ? 'All-Time Spend' : `${year} Spend`);
    setText('js-stat-value-total', '$' + number_format(stats.total ?? 0, 2));

    // Card 2: YTD / Year-vs-PrevYear
    setText('js-stat-label-ytd', isAllYears ? 'YTD Spend' : `YTD Spend`);
    if (isAllYears) {
        setText('js-stat-value-ytd', '$' + number_format(stats.ytd ?? 0, 2));
        setHTML('js-stat-badge-ytd', badge(stats.ytd_change ?? null));
    } else {
        setText('js-stat-value-ytd', '$' + number_format(stats.total ?? 0, 2));
        setHTML('js-stat-badge-ytd', badge(stats.change ?? null));
    }

    // Card 3: MTD / Best Month
    // Phase 158 folded-todo fix: per-year payloads lack mtd/mtd_change keys (they are only
    // in the 'All Years' payload from getStats()). When the current year is selected,
    // fall back to txn_payload['All Years'].stats.mtd — correct because all-time MTD = current-year MTD by definition.
    if (isAllYears || isCurrentYear) {
        setText('js-stat-label-mtd', 'MTD Spend');
        const allYearsStats = (typeof txn_payload !== 'undefined' && txn_payload['All Years']?.stats) ?? {};
        const mtdValue     = isAllYears ? (stats.mtd ?? 0) : (allYearsStats.mtd ?? 0);
        const mtdChange    = isAllYears ? (stats.mtd_change ?? null) : (allYearsStats.mtd_change ?? null);
        setText('js-stat-value-mtd', '$' + number_format(mtdValue, 2));
        setHTML('js-stat-badge-mtd', badge(mtdChange));
    } else {
        setText('js-stat-label-mtd', `Best Month ${year}`);
        if (stats.best_month) {
            setText('js-stat-value-mtd',
                `${stats.best_month} — $${number_format(stats.best_month_total ?? 0, 2)}`);
        } else {
            setText('js-stat-value-mtd', '$0.00');
        }
        setHTML('js-stat-badge-mtd', '');
    }

    // Card 4: Count
    setText('js-stat-label-count', 'Transactions');
    setText('js-stat-value-count', String(stats.count ?? 0));

    // Card 5: Avg
    setText('js-stat-label-avg', 'Avg / Transaction');
    setText('js-stat-value-avg', '$' + number_format(stats.avg ?? 0, 2));
}

function onYearChange(year: string): void {
    const yearData = txn_payload[year];
    if (!yearData) return;
    currentYear = year;

    // --- Bar chart: level-aware re-render (D-06 — toggle composes with year-select) ---
    const barChart = getChartById('chart_txn_monthly');
    if (barChart) {
        renderBarForLevel(barChart, yearData, year);
    }

    // --- Donut chart: D-03 reset — drill up if drilled, replace data + drilldown.series, setSubtitle ---
    const donutChart = getChartById('chart_txn_donut');
    if (donutChart) {
        // Drill up until at main level (drilldownLevels is internal Highcharts state)
        while ((donutChart as any).drilldownLevels?.length > 0) {
            donutChart.drillUp();
        }
        const byMain = yearData.by_main || [];
        const total = byMain.reduce((s: number, d: any) => s + d.total, 0);
        if (donutChart.series[0]) {
            donutChart.series[0].setData(
                byMain.map((d: any) => {
                    const p: any = { name: d.main_name, y: d.total, ...(d.main_id !== 0 ? { drilldown: String(d.main_id) } : {}) };
                    if (d.main_color) p.color = d.main_color;  // conditional spread — never explicit undefined
                    return p;
                }),
                false  // redraw=false — batch with update() below, single redraw at end
            );
        }
        // Replace stale drilldown.series from previous year (Pitfall 3 — chart.update() is the documented fix)
        (donutChart as any).update({ drilldown: { series: buildDonutDrillSeries(yearData) } }, false);
        donutChart.setSubtitle({ text: abbreviateCurrency(total) });
        donutChart.redraw();
    }

    // --- Stat cards ---
    updateStatCards(year, yearData.stats);
}

function stats() {
    if (typeof txn_donut === 'undefined') return;

    // Phase 181: theme ternary blocks removed; getChartTheme() supplies all chrome via merge.
    // Data-series identity colors (main_color, groupColor) remain FIXED (D-01).

    currentYear = String(txn_current_year);

    const initialYearData = txn_payload[currentYear] ?? txn_payload['All Years'];
    const initialByMain = initialYearData.by_main || [];
    const initialDonutTotal = initialByMain.reduce((s: number, d: any) => s + d.total, 0);

    // Donut chart — spending by main category with native drilldown into subs (D-01/D-02)
    Highcharts.chart('chart_txn_donut', Highcharts.merge(getChartTheme(), {
        chart: {
            type: 'pie',
            height: 350,
            events: {
                // D-03: drilling updates center subtitle to drilled main's total
                drilldown: function(this: Highcharts.Chart, e: any) {
                    const subTotal = (e.seriesOptions.data as any[]).reduce((s: number, p: any) => s + p.y, 0);
                    this.setSubtitle({ text: abbreviateCurrency(subTotal) });
                },
                // D-03: drillup resets center subtitle to main-level grand total
                drillup: function(this: Highcharts.Chart) {
                    const yearData = txn_payload[currentYear] ?? txn_payload['All Years'];
                    const mainTotal = (yearData.by_main || []).reduce((s: number, d: any) => s + d.total, 0);
                    this.setSubtitle({ text: abbreviateCurrency(mainTotal) });
                }
            }
        },
        title: { text: 'Spending by Category', style: { fontSize: '14px' } },
        subtitle: {
            text: abbreviateCurrency(initialDonutTotal),
            floating: true,
            verticalAlign: 'middle',
            y: 10,
            style: { fontSize: '16px', fontWeight: 'bold' }
        },
        legend: {
            enabled: true,
            layout: 'vertical' as const,
            align: 'right' as const,
            verticalAlign: 'middle' as const,
        },
        plotOptions: {
            pie: {
                innerSize: '60%',
                showInLegend: true,
                dataLabels: { enabled: false },
            }
        },
        series: [{
            type: 'pie',
            name: 'Amount',
            data: initialByMain.map((d: any) => {
                const p: any = { name: d.main_name, y: d.total, ...(d.main_id !== 0 ? { drilldown: String(d.main_id) } : {}) };
                if (d.main_color) p.color = d.main_color;  // conditional spread — never explicit undefined (project memory)
                return p;
            })
        }],
        drilldown: {
            series: buildDonutDrillSeries(initialYearData),
            // Phase 181: breadcrumb text color comes from getChartTheme() xAxis.title.style.color
            // (textPrimary token) — no hardcoded foreground needed.
            breadcrumbs: {
                position: { align: 'left' },
                buttonTheme: {
                    style: {},
                    states: { hover: { style: {} }, select: { style: {} } }
                },
                separator: { style: {} }
            }
        },
        credits: { enabled: false }
    } as any));

    // Stacked column chart — monthly spending by category (defaults to Main level, D-04)
    const yearData = txn_payload[currentYear] ?? txn_payload['All Years'];
    const initialBarSeries = buildMainSeries(yearData);  // default Main level
    const initialBarCats = (Object.values(yearData.monthly_by_main || {}) as any[][])[0]?.map((r: any) => r.month)
        ?? getMonthCategories(yearData, currentYear);

    Highcharts.chart('chart_txn_monthly', Highcharts.merge(getChartTheme(), {
        chart: { type: 'column', height: 380 },
        title: { text: 'Monthly Spending by Category', style: { fontSize: '14px' } },
        xAxis: {
            type: 'category',
            categories: initialBarCats,
            labels: { rotation: -45 },
        },
        yAxis: {
            title: { text: 'Amount ($)' },
            gridLineWidth: 0.1,
        },
        tooltip: {
            shared: true,
            // Categorical x-axis: this.x is the month label string (e.g. "Jan 2026"),
            // NOT a timestamp — override the helper's default date header so it
            // doesn't run Highcharts.dateFormat on a string (RESEARCH Q3).
            formatter: makeEmptyAwareTooltip({
                shared: true,
                formatHeader: (ctx) => `<b>${ctx.x}</b>`,
            }),
        },
        legend: {
            enabled: true,
            layout: 'horizontal' as const,
            align: 'center' as const,
            verticalAlign: 'bottom' as const,
        },
        plotOptions: {
            column: {
                stacking: 'normal',
                borderWidth: 0,
                states: { hover: { borderWidth: 1 } },
            },
            // NO point.events.click — DASH-03 out of scope (success criterion #5).
        },
        series: initialBarSeries,
        credits: { enabled: false },
    } as any));

    // Wire year-select change listener
    const yearSelect = document.getElementById('js-year-select') as HTMLSelectElement | null;
    if (yearSelect) {
        yearSelect.addEventListener('change', () => {
            onYearChange(yearSelect.value);
        });
    }

    // Wire Main|Sub toggle (D-04/D-06): re-render bar at selected level for current year
    const barToggle = document.getElementById('js-bar-level-toggle');
    if (barToggle) {
        barToggle.addEventListener('click', (e: Event) => {
            const btn = (e.target as HTMLElement).closest('button[data-level]') as HTMLButtonElement | null;
            if (!btn) return;
            const level = btn.getAttribute('data-level') as 'main' | 'sub' | null;
            if (!level || level === barLevel) return;
            barLevel = level;
            // Move .active class to clicked button
            barToggle.querySelectorAll('button[data-level]').forEach(b => b.classList.remove('active'));
            btn.classList.add('active');
            // Re-render bar for current year at new level
            const bar = getChartById('chart_txn_monthly');
            if (bar) {
                const yd = txn_payload[currentYear] ?? txn_payload['All Years'];
                renderBarForLevel(bar, yd, currentYear);
            }
        });
    }

    // On initial page load: if currentYear is NOT 'All Years' AND the server-rendered
    // stat cards came from getStats() (which is all-time), we must overwrite them
    // with the current-year payload so the page matches the selected year on first paint.
    if (currentYear !== 'All Years' && txn_payload[currentYear]) {
        updateStatCards(currentYear, txn_payload[currentYear].stats);
    }
}

let datatable : any;

// Phase 158 D-14: per-session flow toggle state (reset on page reload; no localStorage/cookie).
// Default = debit-only (spending view). Toggle shows all transactions (debit + credit + income).
let showAll = false;

/**
 * Render the child-row item breakdown for a split-parent transaction.
 *
 * Called from the DataTables row.child() API with the splits array from the row data.
 * All data is embedded in the row JSON — NO AJAX per-row (N+1 prevention, RD-06).
 *
 * GATED zero-amount display: when item.amount == 0, render "— CAD" and add a note
 * row ("Item amounts pending official Amazon export.") — never shows $0.00 (RD-04).
 *
 * Phase 158 D-10/D-11: for manual splits (isAmazonSplit=false), footer note changes to
 * "To edit the split, use the Edit button." and per-item recategorize link is removed.
 * For Amazon splits, existing read-only recategorize pattern is preserved.
 */
function renderSplitChildren(splits: any[], isAmazonSplit: boolean = true): string {
    if (!splits || splits.length === 0) {
        return '<div class="p-2 ps-4"><p class="text-muted small mb-0">No item breakdown available.</p></div>';
    }

    const allZero = splits.every((s: any) => parseFloat(s.amount) === 0);

    let rows = splits.map((item: any) => {
        const amt = parseFloat(item.amount);
        const amtCell = (amt === 0)
            ? '<td class="text-end small text-muted">— CAD</td>'
            : `<td class="text-end small text-danger">$${number_format(amt, 2)} CAD</td>`;
        const catName = item.sub_cat_name || 'Uncategorized';
        // For Amazon splits: show recategorize button (D-11). For manual splits: read-only category badge.
        const catCell = isAmazonSplit
            ? `<td><button type="button" class="btn btn-link btn-sm p-0 text-start text-decoration-none js-split-cat-edit" data-split-id="${item.id}" data-sub-cat-id="${item.sub_cat_id ?? ''}" title="Change category"><span class="badge border text-body-secondary">${catName}</span> <span class="text-muted small">&#9998;</span></button></td>`
            : `<td><span class="badge border text-body-secondary">${catName}</span></td>`;
        return `<tr>
            <td class="small" style="max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" title="${item.description}">${item.description}</td>
            ${catCell}
            ${amtCell}
        </tr>`;
    }).join('');

    // Add gated pending-amounts note row when all amounts are zero
    if (allZero) {
        rows += `<tr><td colspan="3" class="text-muted small fst-italic">Item amounts pending official Amazon export.</td></tr>`;
    }

    // Footer total (hidden when all amounts are zero)
    let tfoot = '';
    if (!allZero) {
        const total = splits.reduce((s: number, item: any) => s + parseFloat(item.amount || '0'), 0);
        tfoot = `<tfoot><tr class="fw-semibold">
            <td colspan="2" class="small fw-semibold text-end">Total</td>
            <td class="text-end small fw-semibold text-danger">$${number_format(total, 2)} CAD</td>
        </tr></tfoot>`;
    }

    const reviewUrl = document.querySelector('[data-review-url]')?.getAttribute('data-review-url') ?? '/transactions/review';

    // D-10/D-11: footer note differs by split type
    const footerNote = isAmazonSplit
        ? `Read-only. To recategorize an item, use the <a href="${reviewUrl}">Review Queue</a>.`
        : `To edit the split, use the Edit button.`;

    return `<div class="p-2 ps-4">
        <p class="small fw-semibold mb-2 text-muted">Order Items</p>
        <div class="table-responsive">
            <table class="table table-sm table-bordered mb-0">
                <thead>
                    <tr><th>Item</th><th>Category</th><th>Amount</th></tr>
                </thead>
                <tbody>${rows}</tbody>
                ${tfoot}
            </table>
        </div>
        <p class="text-muted small mt-1 mb-0">${footerNote}</p>
    </div>`;
}

function list() {
    let table = document.querySelector('.datatable[data-table="transactions_list"]') as HTMLElement;
    if(!table) {
        return;
    }

    // 158 UAT: expand/collapse chevrons (used by the row action cluster + the expand handler).
    const CHEVRON_RIGHT = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/></svg>';
    const CHEVRON_DOWN  = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/></svg>';

    datatable = new DataTable('.datatable[data-table="transactions_list"]',{
        pageLength: 20,
        lengthMenu: [10,20,50,100],
        processing: true,
        serverSide: true,
        ajax: {
            url: '?do=getTransactions',
            data: function(data, settings) {
                let search = settings.oPreviousSearch.search;

                let search_attr = table.getAttribute('data-search') as string;
                if(search_attr && search === '') {
                    data.search.value = search_attr;
                    search = search_attr;
                }

                // 158 UAT: fetch BOTH the spending (debit) and credit/income totals in one
                // request and update both header spans (kept gross/separate — not netted).
                let total = document.querySelector('.js-transactions-total') as HTMLElement;
                let totalCredit = document.querySelector('.js-transactions-total-credit') as HTMLElement;
                if(total) {
                    get('?do=getTotals', {search: search}, (data: any) => {
                        if(data) {
                            total.innerText = number_format(data.debit ?? 0);
                            if(totalCredit) totalCredit.innerText = number_format(data.creditIncome ?? 0);
                        }
                    });
                }
                // Phase 158 D-14: send flow toggle state so server applies/removes debit-only filter
                // data.showAll consumed by server-side DataTable handler (Plan 03 Task 2)
                data.showAll = showAll ? '1' : '0';
                return data;
            }
        },
        columns: [
            { data: 'type' },
            { data: 'account', render: function(acct: any) {
                // 158 UAT: mask account to the last 4 digits with asterisks; Simplii sends "0"
                // (or blank) for accounts it doesn't expose — show an em dash instead of "0".
                const s = (acct ?? '').toString().trim();
                if (s === '' || /^0+$/.test(s)) return '<span class="text-muted">—</span>';
                return '<span class="font-monospace">****' + s.slice(-4) + '</span>';
            } },
            { data: 'date' },
            { data: null, render: function(data) {
                // Append "Split" badge on the Name column for split-parent rows
                const name = data.name ?? '';
                const key  = data.merchant_key ?? '';
                // D-02: link to merchant deep-dive page using server-provided normalized key (MERCH-01)
                // encodeURIComponent handles spaces/special chars in normalized keys
                const nameHtml = key
                    ? `<a href="/transactions/merchant?name=${encodeURIComponent(key)}">${name}</a>`
                    : name;
                let badges = '';
                // 158 UAT: dropped the "Split" badge — split rows are already signalled by the
                // expand (+/-) control and the Edit/Unsplit action buttons, so it was redundant.
                // D-05 audit marker: auto badge for e-transfer memory auto-applied transactions
                if (data.auto_applied_memory === 1) {
                    badges += ' <span class="badge bg-secondary bg-opacity-10 text-secondary ms-1" title="Category set automatically by e-transfer recipient memory">auto</span>';
                }
                // 158 UAT — flow indicator (matches merchant-page D-08 badge styling). Credit/refund
                // and income rows are flagged so they're distinguishable when "Show all" is on; debit
                // rows stay clean since the list is a spending (debit) view by default.
                if (data.flow === 'credit') {
                    badges += ' <span class="badge bg-success bg-opacity-10 text-success ms-1" title="Credit / refund — not counted in spending">Credit</span>';
                } else if (data.flow === 'income') {
                    badges += ' <span class="badge bg-info bg-opacity-10 text-info ms-1" title="Income — not counted in spending">Income</span>';
                }
                return nameHtml + badges;
            }},
            // Phase 196 (TRACK-08/D-11) — "view tracking" affordance placed right after Name:
            // parcel/truck icon color-coded by the linked parcel's most-actionable status bucket;
            // opens the parcel(s). tracking_status/tracking_ids attached server-side by
            // TransactionsModel::getTransactionsTable() (guarded LEFT JOIN, empty when unlinked).
            // Icon sized to 1em (matches row font height); color via Bootstrap text-* utility.
            { data: null, orderable: false, render: function(data) {
                const ids: number[] = data.tracking_ids || [];
                if (!ids.length) return '';
                const status = data.tracking_status || '';
                const colorClass = (status === 'Exception' || status === 'DeliveryFailure') ? 'text-danger'
                    : (status === 'Delivered') ? 'text-success'
                    : 'text-warning'; // OutForDelivery / InTransit / InfoReceived bucket
                const title = ids.length > 1 ? (ids.length + ' linked parcels') : 'View tracking';
                return '<a href="/tracking/' + ids[0] + '" class="' + colorClass + '" title="' + title + '">'
                     + '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" '
                     + 'style="height:1em;width:0.875em;vertical-align:-0.125em" fill="currentColor" aria-hidden="true"><path d="M50.7 58.5L0 160H208V32H93.7c-18.2 0-34.8 10.3-43 26.5zM240 160h208L397.3 58.5C389.1 42.3 372.5 32 354.3 32H240V160zm208 32H0l0 208c0 26.5 21.5 48 48 48l352 0c26.5 0 48-21.5 48-48l0-208z"/></svg>'
                     + '</a>';
            }},
            //{ data: 'description' },
            { data: null, orderable: false, render: function(data) {
                // 158 UAT — Category column. Resolve sub_cat_id → sub-category name via the
                // injected categoryTree. Split parents show "(split)" (their children carry the
                // categories); other rows are click-to-recategorize (.js-txn-cat-edit → #txnCatModal).
                if (data.is_split_parent === 1) {
                    // 158 UAT: the "(split)" label IS the expand/collapse toggle for the line items
                    // (the separate chevron button was removed to save space). Reuses .js-split-expand.
                    return '<a href="#" role="button" aria-expanded="false" '
                         + 'class="js-split-expand text-decoration-none text-muted small" '
                         + 'title="Show / hide line items">' + CHEVRON_RIGHT + ' (split)</a>';
                }
                const tree: any[] = (window as any).categoryTree || [];
                const subId = data.sub_cat_id != null ? parseInt(data.sub_cat_id, 10) : 0;
                let name = '';
                for (const main of tree) {
                    const hit = (main.subs || []).find((s: any) => s.id === subId);
                    if (hit) { name = hit.name; break; }
                }
                const label = name
                    ? '<span class="badge bg-secondary bg-opacity-10 text-body">' + name + '</span>'
                    : '<span class="text-muted">Uncategorized</span>';
                return '<a href="#" class="js-txn-cat-edit text-decoration-none" data-txn-id="'
                     + (data.id ?? '') + '" data-sub-cat-id="' + subId
                     + '" title="Click to change category">' + label + '</a>';
            }},
            { data: null, className: 'js-txn-amount-cell', render: function(data) {
                return '$'+number_format(data.amount,2);
            }},
        ],
        order: [[2,"desc"]] as Order[],
        // Phase 183 (UX-02, D-09/D-10): empty-state for 0-row result sets.
        // DataTables server-side AJAX delivers the 0-row guard for this table
        // (there is no Latte foreach — rows arrive via AJAX). The language.emptyTable
        // HTML mirrors the .ksat-empty-state component style (T-183-09: guards $row->undeclared).
        language: {
            emptyTable: '<div class="ksat-empty-state text-center py-5">' +
                '<div class="ksat-empty-state__icon mb-3" aria-hidden="true">' +
                '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" style="opacity:.35;color:var(--bs-secondary-color)" viewBox="0 0 16 16" role="img" aria-hidden="true">' +
                '<path d="M4.98 4a.5.5 0 0 0-.39.188L1.54 8H6a.5.5 0 0 1 .5.5 1.5 1.5 0 1 0 3 0A.5.5 0 0 1 10 8h4.46l-3.05-3.812A.5.5 0 0 0 11.02 4H4.98zm9.954 5H10.45a2.5 2.5 0 0 1-4.9 0H1.066l.32 2.562A.5.5 0 0 0 1.88 12h12.24a.5.5 0 0 0 .496-.438L14.933 9zM3.809 3.563A1.5 1.5 0 0 1 4.981 3H11.02a1.5 1.5 0 0 1 1.172.563l3.7 4.625a.5.5 0 0 1 .105.374l-.39 3.124A1.5 1.5 0 0 1 14.117 13H1.883a1.5 1.5 0 0 1-1.489-1.314L.004 8.562a.5.5 0 0 1 .105-.374l3.7-4.625z"/>' +
                '</svg></div>' +
                '<p class="text-muted mb-3">No transactions found. Try adjusting your date range or import a CSV.</p>' +
                '<a href="/transactions/import" class="btn btn-outline-secondary btn-sm">Import CSV</a>' +
                '</div>',
        },
        // Phase 183 (UX-02, D-08/D-10): skeleton + empty-state via drawCallback (v2 DataTables).
        // MEMORY: DataTables redraw → use drawCallback (v2, native draw listeners dead).
        // After each AJAX draw: hide skeleton; show/hide empty-state based on row count.
        drawCallback: function() {
            // Hide skeleton once data (or empty-state) renders after each AJAX draw
            const skel = document.querySelector('.ksat-transactions-skeleton') as HTMLElement | null;
            if (skel) skel.hidden = true;
            // Show the Latte-rendered empty-state when 0 rows are returned (T-183-09).
            // Phase 183 hotfix: inside a DataTables drawCallback `this` is the settings object,
            // NOT the API — so `this.page` was undefined and threw, which stalled DataTables'
            // own processing indicator ("stuck on loading"). Use the outer `datatable` API
            // instance (assigned synchronously before the async draw fires) instead.
            const empty = document.querySelector('.ksat-transactions-empty') as HTMLElement | null;
            if (empty && datatable) {
                const rowCount = datatable.page.info().recordsDisplay;
                empty.hidden = rowCount > 0;
            }
        },
        rowCallback: function(row: HTMLElement, data: any) {
            // 158 UAT: the expand/collapse control used to be PREPENDED to the type column,
            // which looked odd — only split rows had it, so the column misaligned. It now lives
            // in the action cluster (chevron added below), keeping the type column uniform.

            // Phase 158 D-08/D-09/D-11 — Row action buttons in the last cell.
            // eligibility gate: flow IN ('debit','credit') AND not already a split parent → "Split"
            // Manual split parent (is_split_parent=1, is_amazon_split=false) → "Edit" + "Unsplit"
            // Amazon split parent (is_split_parent=1, is_amazon_split=true) → "Unsplit" only
            // income / no-split rows → no action button
            // Phase 196 fix: the action cluster belongs on the AMOUNT line, not literally the
            // last cell — Wave 4 added a Tracking column AFTER Amount, so `td:last-child` now
            // points at the tracking cell and would clobber the "view tracking" truck. Target the
            // amount cell explicitly (js-txn-amount-cell) so the tracking column is left intact.
            const lastTd = row.querySelector('td.js-txn-amount-cell') as HTMLTableCellElement | null;
            if (!lastTd) return;

            const flow = data.flow ?? '';
            const isSplitParent = data.is_split_parent === 1;
            const isAmazonSplit = !!data.is_amazon_split;
            const txnId = data.id ?? '';
            const txnAmount = parseFloat(data.amount ?? '0');
            const txnDesc = (data.name ?? '') + ' — $' + number_format(txnAmount, 2) + ' CAD';
            const subCatId = parseInt(data.sub_cat_id ?? '0', 10);

            // 158 UAT: keep the action buttons on the SAME line as the amount and make them
            // extra-small. Bootstrap btn CSS-vars shrink padding/font below btn-sm.
            const SMALL_BTN = '--bs-btn-padding-y:.05rem;--bs-btn-padding-x:.45rem;--bs-btn-font-size:.72rem;line-height:1.35;';
            const actionWrapper = document.createElement('div');
            actionWrapper.className = 'd-inline-flex gap-1 flex-nowrap';

            // 158 UAT: the expand/collapse toggle for split line items now lives on the "(split)"
            // label in the Category column (saves action-cluster space) — see the category render.

            if (!isSplitParent && (flow === 'debit' || flow === 'credit')) {
                // Eligible unsplit row → "Split" button (D-09)
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'btn btn-outline-secondary js-split-open';
                btn.style.cssText = SMALL_BTN;
                btn.dataset.txnId = String(txnId);
                btn.dataset.txnAmount = String(txnAmount);
                btn.dataset.txnDesc = txnDesc;
                btn.dataset.subCatId = String(subCatId);
                btn.textContent = 'Split';
                actionWrapper.appendChild(btn);
            } else if (isSplitParent && !isAmazonSplit) {
                // Manually-split parent → "Edit" + "Unsplit" (D-09/D-10)
                const editBtn = document.createElement('button');
                editBtn.type = 'button';
                editBtn.className = 'btn btn-outline-secondary js-edit-split';
                editBtn.style.cssText = SMALL_BTN;
                editBtn.dataset.txnId = String(txnId);
                editBtn.dataset.txnAmount = String(txnAmount);
                editBtn.dataset.txnDesc = txnDesc;
                editBtn.dataset.subCatId = String(subCatId);
                editBtn.textContent = 'Edit';
                actionWrapper.appendChild(editBtn);

                const unsplitBtn = document.createElement('button');
                unsplitBtn.type = 'button';
                unsplitBtn.className = 'btn btn-outline-danger js-unsplit';
                unsplitBtn.style.cssText = SMALL_BTN;
                unsplitBtn.dataset.txnId = String(txnId);
                unsplitBtn.textContent = 'Unsplit';
                actionWrapper.appendChild(unsplitBtn);
            } else if (isSplitParent && isAmazonSplit) {
                // Amazon-split parent → "Unsplit" only (D-11)
                const unsplitBtn = document.createElement('button');
                unsplitBtn.type = 'button';
                unsplitBtn.className = 'btn btn-outline-danger js-unsplit';
                unsplitBtn.style.cssText = SMALL_BTN;
                unsplitBtn.dataset.txnId = String(txnId);
                unsplitBtn.textContent = 'Unsplit';
                actionWrapper.appendChild(unsplitBtn);
            }
            // income rows (flow='income') → no action button (D-08)

            if (actionWrapper.children.length > 0) {
                // Put amount + buttons on ONE line: rewrap the amount text into a flex row
                // alongside the buttons (158 UAT — buttons were dropping below the amount).
                const amountText = (lastTd.textContent || '').trim();
                lastTd.textContent = '';
                const cellFlex = document.createElement('div');
                cellFlex.className = 'd-flex align-items-center gap-2 flex-nowrap';
                const amtSpan = document.createElement('span');
                amtSpan.className = 'text-nowrap';
                amtSpan.textContent = amountText;
                cellFlex.appendChild(amtSpan);
                cellFlex.appendChild(actionWrapper);
                lastTd.appendChild(cellFlex);
            }
        }
    });

    // Phase 183 (UX-02, D-08/D-10): Show skeleton shimmer immediately on page load
    // while the DataTable's first AJAX fetch is in-flight. drawCallback hides it.
    const txnSkel = document.querySelector('.ksat-transactions-skeleton') as HTMLElement | null;
    if (txnSkel) txnSkel.hidden = false;

    // ── Child-row expand handler (split parents only, gated to list page) ─────
    // Delegated click on the DataTable wrapper so it works after ajax.reload().
    // data-table="transactions_list" check guards against double-bind on other pages
    // (Pitfall 5 — index.ts init()s every module on every page; T-152-11).
    const tableWrapper = document.querySelector('.datatable[data-table="transactions_list"]');
    if (!tableWrapper) return;

    tableWrapper.addEventListener('click', function(e: Event) {
        const btn = (e.target as HTMLElement).closest('.js-split-expand') as HTMLElement | null;
        if (!btn) return;
        e.preventDefault(); // the toggle is now an <a> ("(split)") in the Category column

        const tr = btn.closest('tr') as HTMLTableRowElement | null;
        if (!tr) return;

        const row = datatable.row(tr);
        const rowData: any = row.data();
        if (!rowData || rowData.is_split_parent !== 1) return;

        // D-10/D-11: pass is_amazon_split flag to renderSplitChildren
        const isAmazonSplit = !!rowData.is_amazon_split;

        if (row.child.isShown()) {
            row.child.hide();
            btn.setAttribute('aria-expanded', 'false');
            btn.innerHTML = CHEVRON_RIGHT + ' (split)';
        } else {
            row.child(renderSplitChildren(rowData.splits || [], isAmazonSplit)).show();
            btn.setAttribute('aria-expanded', 'true');
            btn.innerHTML = CHEVRON_DOWN + ' (split)';
        }
    });

    // ── Phase 158 D-14: Flow toggle handler (per-session showAll state) ──────
    // Flips showAll, updates button label, reloads DataTable with new showAll param.
    // Composes with existing Phase 154 mainCatId/subCatId filters (independent params).
    const flowToggleBtn = document.querySelector('.js-flow-toggle') as HTMLButtonElement | null;
    if (flowToggleBtn) {
        flowToggleBtn.addEventListener('click', () => {
            showAll = !showAll;
            flowToggleBtn.textContent = showAll ? 'Show all transactions' : 'Spending only';
            flowToggleBtn.dataset.showAll = showAll ? '1' : '0';
            if (datatable) datatable.ajax.reload();
        });
    }
}

function undoButtons() {
    // Use event delegation on document so it works regardless of when the DOM renders.
    document.addEventListener('click', (e: Event) => {
        const target = e.target as HTMLElement;
        const btn = target.closest('.js-undo-batch') as HTMLElement | null;
        if (!btn) return;

        const batchId = btn.getAttribute('data-batch-id');
        if (!batchId) return;

        if (!confirm(`Undo batch #${batchId}? All its transactions will be permanently removed.`)) {
            return;
        }

        btn.setAttribute('disabled', 'disabled');
        btn.textContent = 'Undoing…';

        const body = new URLSearchParams();
        body.set('batchId', batchId);

        fetch('?do=undoBatch', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        })
        .then(r => r.json())
        .then((data: any) => {
            const resultEl = document.querySelector('.js-undo-result') as HTMLElement | null;
            if (data.success) {
                // Remove the row from the recent-imports table
                const row = btn.closest('tr');
                if (row) row.remove();
                if (resultEl) {
                    resultEl.classList.remove('d-none');
                    resultEl.innerHTML = `<div class="alert alert-success">Batch #${batchId} undone — ${data.deleted} row${data.deleted !== 1 ? 's' : ''} removed.</div>`;
                }
                // Reload the DataTable to reflect deleted transactions
                if (typeof datatable !== 'undefined' && datatable) {
                    datatable.ajax.reload();
                }
            } else {
                btn.removeAttribute('disabled');
                btn.textContent = 'Undo';
                if (resultEl) {
                    resultEl.classList.remove('d-none');
                    resultEl.innerHTML = `<div class="alert alert-danger">Undo failed: ${data.message ?? 'Unknown error'}</div>`;
                }
            }
        })
        .catch(() => {
            btn.removeAttribute('disabled');
            btn.textContent = 'Undo';
            const resultEl = document.querySelector('.js-undo-result') as HTMLElement | null;
            if (resultEl) {
                resultEl.classList.remove('d-none');
                resultEl.innerHTML = '<div class="alert alert-danger">An error occurred while undoing the batch.</div>';
            }
        });
    });
}

function review() {
    const cardList = document.querySelector('#review-card-list') as HTMLElement | null;
    if (!cardList) return;

    // Helper: update nav badge spans to the given count; remove span entirely when 0
    function updateBadge(pendingCount: number): void {
        document.querySelectorAll('.nav-link .badge, .nav-tabs .badge, li > a > .badge').forEach((el) => {
            // Only update the Review badge — identified by nearest link containing "review"
            const link = el.closest('a');
            if (link && (link.href.includes('/transactions/review') || link.textContent?.toLowerCase().includes('review'))) {
                if (pendingCount === 0) {
                    el.remove();
                } else {
                    el.textContent = String(pendingCount);
                }
            }
        });
    }

    // Helper: vanish-animate a card then remove it from DOM
    function vanishCard(card: HTMLElement, callback?: () => void): void {
        card.setAttribute('aria-hidden', 'true');
        card.style.transition = 'opacity 0.3s, transform 0.3s';
        card.style.opacity = '0';
        card.style.transform = 'translateY(-8px)';
        setTimeout(() => {
            card.remove();
            if (callback) callback();
        }, 300);
    }

    // Helper: check if all cards are gone and show empty state
    function maybeShowEmptyState(pendingCount: number): void {
        const remaining = cardList.querySelectorAll('.review-card');
        if (remaining.length === 0 || pendingCount === 0) {
            // Show empty state if not already present
            if (!cardList.querySelector('.js-empty-state')) {
                const emptyDiv = document.createElement('div');
                emptyDiv.className = 'col-12 col-md-8 col-lg-6 mx-auto text-center py-5 js-empty-state';
                emptyDiv.innerHTML = `
                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" class="bi bi-check-circle-fill text-success" viewBox="0 0 16 16">
                        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                    </svg>
                    <h5 class="mt-3 fw-semibold">All caught up</h5>
                    <p class="text-muted">No transactions need review right now. Import a CSV to add items.</p>`;
                cardList.appendChild(emptyDiv);
            }
        }
    }

    // Helper: update bulk bar count label + button text + show/hide
    function updateBulkBar(): void {
        const checked = cardList.querySelectorAll('.review-bulk-check:checked');
        const count = checked.length;
        const bar = document.querySelector('#review-bulk-bar') as HTMLElement | null;
        const countLabel = bar?.querySelector('.js-bulk-count-label');
        const confirmBtn = bar?.querySelector('.js-bulk-confirm');
        if (!bar) return;
        if (count === 0) {
            bar.classList.add('d-none');
            cardList.style.paddingBottom = '';
        } else {
            bar.classList.remove('d-none');
            cardList.style.paddingBottom = '80px';
        }
        if (countLabel) {
            countLabel.textContent = count === 1 ? '1 item selected' : `${count} items selected`;
        }
        if (confirmBtn) {
            confirmBtn.textContent = count === 1 ? 'Confirm 1 item' : `Confirm ${count} items`;
        }
    }

    // ── Category select: reveal / hide new-cat fields ──────────────────────
    cardList.addEventListener('change', (e: Event) => {
        const target = e.target as HTMLElement;

        // Category select change
        if (target.classList.contains('review-cat-select')) {
            const card = target.closest('.review-card') as HTMLElement | null;
            if (!card) return;
            const newCatBlock = card.querySelector('.review-new-cat') as HTMLElement | null;
            const newCatName = card.querySelector('.review-new-cat-name') as HTMLInputElement | null;
            const newCatParent = card.querySelector('.review-new-cat-parent') as HTMLSelectElement | null;
            const select = target as HTMLSelectElement;
            if (select.value === '__new__') {
                newCatBlock?.classList.remove('d-none');
                if (newCatName) {
                    newCatName.required = true;
                    const suggestedName = card.dataset.suggestedName ?? '';
                    if (suggestedName) newCatName.value = suggestedName;
                    newCatName.focus();
                }
                if (newCatParent) newCatParent.required = true;
            } else {
                newCatBlock?.classList.add('d-none');
                if (newCatName) { newCatName.required = false; newCatName.value = ''; }
                if (newCatParent) newCatParent.required = false;
            }
        }

        // Save-as-rule checkbox change
        if (target.classList.contains('review-rule-check')) {
            const card = target.closest('.review-card') as HTMLElement | null;
            if (!card) return;
            const kwBlock = card.querySelector('.review-rule-keyword') as HTMLElement | null;
            const kwInput = card.querySelector('.review-rule-kw') as HTMLInputElement | null;
            const checkbox = target as HTMLInputElement;
            if (checkbox.checked) {
                kwBlock?.classList.remove('d-none');
                if (kwInput) {
                    kwInput.value = card.dataset.merchant ?? '';
                    kwInput.focus();
                }
            } else {
                kwBlock?.classList.add('d-none');
            }
        }

        // Bulk checkbox change
        if (target.classList.contains('review-bulk-check')) {
            const card = target.closest('.review-card') as HTMLElement | null;
            if (card) {
                const checkbox = target as HTMLInputElement;
                if (checkbox.checked) {
                    card.classList.add('review-bulk-selected');
                } else {
                    card.classList.remove('review-bulk-selected');
                }
            }
            updateBulkBar();
        }
    });

    // ── Confirm / Reject button clicks ────────────────────────────────────
    cardList.addEventListener('click', (e: Event) => {
        const target = e.target as HTMLElement;

        // Per-item Confirm
        const confirmBtn = target.closest('.review-confirm') as HTMLButtonElement | null;
        if (confirmBtn) {
            const card = confirmBtn.closest('.review-card') as HTMLElement | null;
            if (!card) return;

            const queueId = card.dataset.queueId ?? '';
            const catSelect = card.querySelector('.review-cat-select') as HTMLSelectElement | null;
            const subCatValue = catSelect?.value ?? '';
            const newCatName = (card.querySelector('.review-new-cat-name') as HTMLInputElement | null)?.value.trim() ?? '';
            const newCatParent = (card.querySelector('.review-new-cat-parent') as HTMLSelectElement | null)?.value ?? '';
            const saveRule = (card.querySelector('.review-rule-check') as HTMLInputElement | null)?.checked ? 1 : 0;
            const ruleKeyword = (card.querySelector('.review-rule-kw') as HTMLInputElement | null)?.value.trim() ?? '';

            // Validation
            const isNewCat = subCatValue === '__new__';
            let valid = true;
            if (!subCatValue) valid = false;
            if (isNewCat && (!newCatName || !newCatParent)) valid = false;

            // Remove any previous inline error
            card.querySelectorAll('.review-inline-error').forEach(el => el.remove());
            if (!valid) {
                const errDiv = document.createElement('div');
                errDiv.className = 'alert alert-danger mt-2 py-1 small review-inline-error';
                errDiv.textContent = 'Please select a category.';
                card.querySelector('.card-body')?.appendChild(errDiv);
                return;
            }

            card.classList.add('is-loading');

            // Remember-recipient fields (etransfer cards only — null-guarded for non-etransfer)
            const recipCheck = card.querySelector('.review-recipient-check') as HTMLInputElement | null;
            const saveRecipient = recipCheck?.checked ? 1 : 0;
            const recipientNote = (card.querySelector('.review-recipient-note-input') as HTMLInputElement | null)?.value ?? '';

            const body = new URLSearchParams();
            body.set('queueId', queueId);
            body.set('subCatId', isNewCat ? '0' : subCatValue);
            body.set('saveRule', String(saveRule));
            body.set('ruleKeyword', ruleKeyword);
            body.set('newCatName', isNewCat ? newCatName : '');
            body.set('newCatParentId', isNewCat ? newCatParent : '0');
            body.set('saveRecipient', String(saveRecipient));
            body.set('recipientNote', recipientNote);

            fetch('?do=confirmReview', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: body.toString(),
            })
            .then(r => r.json())
            .then((data: any) => {
                if (data.success) {
                    updateBadge(data.pendingCount);
                    vanishCard(card, () => maybeShowEmptyState(data.pendingCount));
                } else {
                    card.classList.remove('is-loading');
                    const errDiv = document.createElement('div');
                    errDiv.className = 'alert alert-danger mt-2 py-1 small review-inline-error';
                    errDiv.textContent = data.message ?? 'Something went wrong — please try again.';
                    card.querySelector('.card-footer')?.after(errDiv);
                }
            })
            .catch(() => {
                card.classList.remove('is-loading');
                const errDiv = document.createElement('div');
                errDiv.className = 'alert alert-danger mt-2 py-1 small review-inline-error';
                errDiv.textContent = 'Something went wrong — please try again.';
                card.querySelector('.card-footer')?.after(errDiv);
            });
        }

        // Per-item Reject
        const rejectBtn = target.closest('.review-reject') as HTMLButtonElement | null;
        if (rejectBtn) {
            const card = rejectBtn.closest('.review-card') as HTMLElement | null;
            if (!card) return;

            const queueId = card.dataset.queueId ?? '';
            card.classList.add('is-loading');

            const body = new URLSearchParams();
            body.set('queueId', queueId);

            fetch('?do=rejectReview', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: body.toString(),
            })
            .then(r => r.json())
            .then((data: any) => {
                if (data.success) {
                    updateBadge(data.pendingCount);
                    vanishCard(card, () => maybeShowEmptyState(data.pendingCount));
                } else {
                    card.classList.remove('is-loading');
                    const errDiv = document.createElement('div');
                    errDiv.className = 'alert alert-danger mt-2 py-1 small review-inline-error';
                    errDiv.textContent = data.message ?? 'Something went wrong — please try again.';
                    card.querySelector('.card-footer')?.after(errDiv);
                }
            })
            .catch(() => {
                card.classList.remove('is-loading');
                const errDiv = document.createElement('div');
                errDiv.className = 'alert alert-danger mt-2 py-1 small review-inline-error';
                errDiv.textContent = 'Something went wrong — please try again.';
                card.querySelector('.card-footer')?.after(errDiv);
            });
        }
    });

    // ── Bulk confirm button ───────────────────────────────────────────────
    const bulkBar = document.querySelector('#review-bulk-bar') as HTMLElement | null;
    bulkBar?.addEventListener('click', (e: Event) => {
        const target = e.target as HTMLElement;
        const bulkBtn = target.closest('.js-bulk-confirm') as HTMLButtonElement | null;
        if (!bulkBtn) return;

        const checked = Array.from(cardList.querySelectorAll('.review-bulk-check:checked')) as HTMLInputElement[];
        if (checked.length === 0) return;

        const queueIds = checked.map(cb => cb.dataset.queueId ?? '').filter(Boolean).join(',');

        const body = new URLSearchParams();
        body.set('queueIds', queueIds);

        fetch('?do=bulkConfirmReview', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        })
        .then(r => r.json())
        .then((data: any) => {
            if (data.success) {
                updateBadge(data.pendingCount);
                // Vanish all checked cards
                const checkedCards = checked.map(cb => cb.closest('.review-card') as HTMLElement | null).filter(Boolean) as HTMLElement[];
                let vanished = 0;
                checkedCards.forEach(card => {
                    vanishCard(card, () => {
                        vanished++;
                        if (vanished === checkedCards.length) {
                            maybeShowEmptyState(data.pendingCount);
                        }
                    });
                });
                // Hide the bulk bar
                bulkBar.classList.add('d-none');
                cardList.style.paddingBottom = '';
            } else {
                const errDiv = document.createElement('div');
                errDiv.className = 'alert alert-danger mt-2 py-1 small';
                errDiv.textContent = data.message ?? 'Something went wrong — please try again.';
                bulkBar.querySelector('.container')?.appendChild(errDiv);
            }
        })
        .catch(() => {
            const errDiv = document.createElement('div');
            errDiv.className = 'alert alert-danger mt-2 py-1 small';
            errDiv.textContent = 'Something went wrong — please try again.';
            bulkBar.querySelector('.container')?.appendChild(errDiv);
        });
    });
}

// ── Recipients management page (Transactions:recipients) ─────────────────────
// Guards: only binds when #recipients-table is in the DOM (project memory: index.ts
// runs all page init()s on every page — guard prevents double-bind on other pages).
// Pitfall: Shared-component init on every page can double-bind + fire wrong-presenter
// signals if not scoped (feedback_shared_component_every_page_init_double_bind).
function initRecipients(): void {
    const table = document.getElementById('recipients-table') as HTMLTableElement | null;
    if (!table) return;  // not on the recipients page

    // Read the categoryTree injected by the Latte template
    const categoryTree: { id: number; name: string; subs: { id: number; name: string }[] }[] =
        (window as any).categoryTree || [];

    // Helper: POST to a URL with a URLSearchParams body; returns parsed JSON
    function postJson(url: string, params: Record<string, string>): Promise<any> {
        const body = new URLSearchParams(params);
        return fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        }).then(r => r.json());
    }

    // Helper: fade out + remove a <tr>
    function fadeRemoveRow(row: HTMLTableRowElement): void {
        row.style.transition = 'opacity 0.25s';
        row.style.opacity = '0';
        setTimeout(() => row.remove(), 260);
    }

    // Helper: build a grouped <select> from categoryTree, pre-selecting a given subCatId
    function buildCatSelect(currentSubCatId: number): HTMLSelectElement {
        const sel = document.createElement('select');
        sel.className = 'form-select form-select-sm js-recip-cat-select';
        categoryTree.forEach(main => {
            const og = document.createElement('optgroup');
            og.label = main.name;
            main.subs.forEach(sub => {
                const opt = document.createElement('option');
                opt.value = String(sub.id);
                opt.textContent = sub.name;
                if (sub.id === currentSubCatId) opt.selected = true;
                og.appendChild(opt);
            });
            sel.appendChild(og);
        });
        return sel;
    }

    // ── Edit flow ─────────────────────────────────────────────────────────────
    // Click Edit → reveal hidden edit sub-row with prefilled form
    table.addEventListener('click', function(e: Event) {
        const editBtn = (e.target as HTMLElement).closest('.js-recip-edit-btn') as HTMLElement | null;
        if (!editBtn) return;

        const recipId    = editBtn.dataset.recipId || '';
        const subCatId   = parseInt(editBtn.dataset.subCatId || '0', 10);
        const desc       = editBtn.dataset.description || '';
        const editUrl    = editBtn.dataset.url || '';
        const dataRow    = table.querySelector(`tr.js-recip-row[data-recip-id="${recipId}"]`) as HTMLTableRowElement | null;
        const editRow    = table.querySelector(`tr.js-recip-edit-row[data-recip-id="${recipId}"]`) as HTMLTableRowElement | null;
        if (!dataRow || !editRow) return;

        // Prefill and reveal the edit row
        const catSelectContainer = editRow.querySelector('.js-recip-cat-select-container') as HTMLElement | null;
        if (catSelectContainer) {
            catSelectContainer.innerHTML = '';
            catSelectContainer.appendChild(buildCatSelect(subCatId));
        }
        const descInput = editRow.querySelector('.js-recip-desc-input') as HTMLInputElement | null;
        if (descInput) descInput.value = desc;

        editRow.classList.remove('d-none');
        editBtn.disabled = true;

        // Save handler
        const saveBtn = editRow.querySelector('.js-recip-save-btn') as HTMLButtonElement | null;
        const cancelBtn = editRow.querySelector('.js-recip-cancel-btn') as HTMLButtonElement | null;
        const errDiv  = dataRow.querySelector('.js-recip-error') as HTMLElement | null;

        function collapseEdit(): void {
            editRow!.classList.add('d-none');
            editBtn.disabled = false;
        }

        if (cancelBtn) {
            cancelBtn.onclick = () => collapseEdit();
        }

        if (saveBtn) {
            saveBtn.onclick = () => {
                const selectedCatSelect = editRow.querySelector('.js-recip-cat-select') as HTMLSelectElement | null;
                const newSubCatId  = selectedCatSelect?.value || '';
                const newDesc      = descInput?.value ?? '';

                saveBtn.disabled = true;
                saveBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';
                if (errDiv) { errDiv.textContent = ''; errDiv.classList.add('d-none'); }

                postJson(editUrl, { id: recipId, subCatId: newSubCatId, description: newDesc })
                    .then((data: any) => {
                        if (data.success) {
                            // Update the visible data row cells in-place
                            // Find the category name from the tree
                            let newMainName = '';
                            let newSubName  = '';
                            const newSubCatIdNum = parseInt(newSubCatId, 10);
                            for (const main of categoryTree) {
                                for (const sub of main.subs) {
                                    if (sub.id === newSubCatIdNum) {
                                        newMainName = main.name;
                                        newSubName  = sub.name;
                                    }
                                }
                            }

                            const catCell = dataRow.querySelector('.js-recip-cat-cell') as HTMLElement | null;
                            if (catCell) catCell.innerHTML = `<span class="text-muted small">${newMainName}</span> › ${newSubName}`;
                            const descCell = dataRow.querySelector('.js-recip-desc-cell') as HTMLElement | null;
                            if (descCell) descCell.textContent = newDesc !== '' ? newDesc : '—';

                            // Update data attributes on the edit button for next time
                            editBtn.dataset.subCatId   = newSubCatId;
                            editBtn.dataset.description = newDesc;

                            collapseEdit();
                        } else {
                            saveBtn.disabled = false;
                            saveBtn.textContent = 'Save';
                            if (errDiv) {
                                errDiv.textContent = 'Failed: ' + (data.message || 'Unknown error') + '. Try again.';
                                errDiv.classList.remove('d-none');
                            }
                        }
                    })
                    .catch(() => {
                        saveBtn.disabled = false;
                        saveBtn.textContent = 'Save';
                        if (errDiv) {
                            errDiv.textContent = 'Failed: network error. Try again.';
                            errDiv.classList.remove('d-none');
                        }
                    });
            };
        }
    });

    // ── Re-apply flow (two-step: dry-run → confirm) ───────────────────────────
    table.addEventListener('click', function(e: Event) {
        const reapplyBtn = (e.target as HTMLElement).closest('.js-recip-reapply-btn') as HTMLButtonElement | null;
        if (!reapplyBtn) return;

        const recipId   = reapplyBtn.dataset.recipId  || '';
        const recipient = reapplyBtn.dataset.recipient || '';
        const reapplyUrl = reapplyBtn.dataset.url || '';
        const td         = reapplyBtn.closest('td') as HTMLTableCellElement | null;
        const dataRow    = reapplyBtn.closest('tr.js-recip-row') as HTMLTableRowElement | null;
        const errDiv     = dataRow?.querySelector('.js-recip-error') as HTMLElement | null;
        if (!td) return;

        // Save original button group HTML for restore
        const originalHtml = td.innerHTML;

        reapplyBtn.disabled = true;
        reapplyBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Checking…';
        if (errDiv) { errDiv.textContent = ''; errDiv.classList.add('d-none'); }

        postJson(reapplyUrl, { id: recipId, dryRun: '1' })
            .then((data: any) => {
                if (!data.success) {
                    td.innerHTML = originalHtml;
                    if (errDiv) {
                        errDiv.textContent = 'Failed: ' + (data.message || 'Unknown error') + '. Try again.';
                        errDiv.classList.remove('d-none');
                    }
                    return;
                }
                const count = data.affected as number;

                if (count === 0) {
                    // Zero result — show info + close
                    td.innerHTML = `
                        <span class="text-muted small">No matching past transactions found.</span>
                        <button class="btn btn-sm btn-link js-recip-reapply-close ms-1">Close</button>`;
                    td.querySelector('.js-recip-reapply-close')?.addEventListener('click', () => {
                        td.innerHTML = originalHtml;
                    });
                } else {
                    // Show confirm prompt
                    td.innerHTML = `
                        <span class="text-muted small">Re-categorize <strong>${count}</strong> past transactions?</span>
                        <button class="btn btn-sm btn-primary js-recip-reapply-confirm ms-1">Confirm</button>
                        <button class="btn btn-sm btn-link js-recip-reapply-cancel">Cancel</button>`;

                    td.querySelector('.js-recip-reapply-cancel')?.addEventListener('click', () => {
                        td.innerHTML = originalHtml;
                    });

                    const confirmBtn = td.querySelector('.js-recip-reapply-confirm') as HTMLButtonElement | null;
                    if (confirmBtn) {
                        confirmBtn.addEventListener('click', () => {
                            confirmBtn.disabled = true;
                            confirmBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';

                            postJson(reapplyUrl, { id: recipId })
                                .then((d2: any) => {
                                    if (d2.success) {
                                        const applied = d2.affected as number;
                                        td.innerHTML = `<span class="text-success small">&#10003; ${applied} transaction${applied !== 1 ? 's' : ''} re-categorized.</span>`;
                                        setTimeout(() => { td.innerHTML = originalHtml; }, 3000);
                                    } else {
                                        td.innerHTML = originalHtml;
                                        if (errDiv) {
                                            errDiv.textContent = 'Failed: ' + (d2.message || 'Unknown error') + '. Try again.';
                                            errDiv.classList.remove('d-none');
                                        }
                                    }
                                })
                                .catch(() => {
                                    td.innerHTML = originalHtml;
                                    if (errDiv) {
                                        errDiv.textContent = 'Failed: network error. Try again.';
                                        errDiv.classList.remove('d-none');
                                    }
                                });
                        });
                    }
                }
            })
            .catch(() => {
                td.innerHTML = originalHtml;
                if (errDiv) {
                    errDiv.textContent = 'Failed: network error. Try again.';
                    errDiv.classList.remove('d-none');
                }
            });
    });

    // ── Delete flow (inline confirm → fade remove) ────────────────────────────
    table.addEventListener('click', function(e: Event) {
        const deleteBtn = (e.target as HTMLElement).closest('.js-recip-delete-btn') as HTMLButtonElement | null;
        if (!deleteBtn) return;

        const recipId   = deleteBtn.dataset.recipId  || '';
        const recipient = deleteBtn.dataset.recipient || '';
        const deleteUrl  = deleteBtn.dataset.url || '';
        const td         = deleteBtn.closest('td') as HTMLTableCellElement | null;
        const dataRow    = deleteBtn.closest('tr.js-recip-row') as HTMLTableRowElement | null;
        const editRow    = dataRow ? (table.querySelector(`tr.js-recip-edit-row[data-recip-id="${recipId}"]`) as HTMLTableRowElement | null) : null;
        const errDiv     = dataRow?.querySelector('.js-recip-error') as HTMLElement | null;
        if (!td || !dataRow) return;

        const originalHtml = td.innerHTML;

        // Show inline confirm
        td.innerHTML = `
            <span class="text-muted small">Remove <strong>${recipient}</strong>?</span>
            <button class="btn btn-sm btn-danger js-recip-delete-confirm ms-1">Yes, delete</button>
            <button class="btn btn-sm btn-link js-recip-delete-cancel">Cancel</button>`;

        td.querySelector('.js-recip-delete-cancel')?.addEventListener('click', () => {
            td.innerHTML = originalHtml;
        });

        const confirmBtn = td.querySelector('.js-recip-delete-confirm') as HTMLButtonElement | null;
        if (confirmBtn) {
            confirmBtn.addEventListener('click', () => {
                confirmBtn.disabled = true;
                confirmBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';

                postJson(deleteUrl, { id: recipId })
                    .then((data: any) => {
                        if (data.success) {
                            // Fade out both data row + edit sub-row
                            if (editRow) {
                                editRow.style.transition = 'opacity 0.25s';
                                editRow.style.opacity = '0';
                                setTimeout(() => editRow.remove(), 260);
                            }
                            fadeRemoveRow(dataRow);
                        } else {
                            td.innerHTML = originalHtml;
                            if (errDiv) {
                                errDiv.textContent = 'Failed: ' + (data.message || 'Unknown error') + '. Try again.';
                                errDiv.classList.remove('d-none');
                            }
                        }
                    })
                    .catch(() => {
                        td.innerHTML = originalHtml;
                        if (errDiv) {
                            errDiv.textContent = 'Failed: network error. Try again.';
                            errDiv.classList.remove('d-none');
                        }
                    });
            });
        }
    });
}

// ── Recurring subscriptions page (Transactions:recurring) ────────────────────
// Guards: only binds when #recurring-table is in the DOM (project memory: index.ts
// runs all page init()s on every page — guard prevents double-bind on other pages).
// Pitfall: Shared-component init on every page can double-bind + fire wrong-presenter
// signals if not scoped (feedback_shared_component_every_page_init_double_bind).
function initRecurring(): void {
    const table = document.getElementById('recurring-table') as HTMLTableElement | null;
    if (!table) return;  // not on the recurring page — no-op guard

    // Initialize client-side DataTable on #recurring-table.
    // Default sort: col 4 (Est. Monthly) descending — largest monthly cost first (D-12).
    // No ajax: config — data is server-rendered in the Latte template.
    new DataTable(table, {
        pageLength: 25,
        order: [[4, 'desc']] as Order[],
        columnDefs: [
            { targets: [2], className: 'text-end' },  // Amount
            { targets: [4], className: 'text-end' },  // Est. Monthly
        ],
    });

    // Initialize Bootstrap tooltips on merchant cells (D-01 reviewable surface).
    // Tooltips show raw name variants on hover/focus (long-press on mobile).
    document.querySelectorAll('#recurring-table [data-bs-toggle="tooltip"]').forEach(el => {
        new (window as any).bootstrap.Tooltip(el);
    });
}

// ── Merchant deep-dive page init (Phase 156) ─────────────────────────────────
// Guards: only binds when #merchant-trend-chart is in the DOM (project memory:
// index.ts runs all page init()s on every page — guard prevents double-bind on
// other pages: feedback_shared_component_every_page_init_double_bind).
function initMerchant(): void {
    if (!document.getElementById('merchant-trend-chart')) return;  // not on merchant page — no-op

    // Phase 181: theme ternary blocks removed; getChartTheme() supplies all chrome via merge.
    // useUTC already set globally in src/index.ts — no per-chart override (feedback_highcharts_useutc_default)
    // events nest under chart:{} NOT at root level (feedback_highcharts_events_nesting_cast_masks)

    // ── Spline: monthly spend trend (UI-SPEC Component 3) ─────────────────
    const trendMonths  = merchant_trend.map((r: any) => r.month);
    const trendAmounts = merchant_trend.map((r: any) => r.total);
    Highcharts.chart('merchant-trend-chart', Highcharts.merge(getChartTheme(), {
        chart: {
            type: 'spline',
            height: 280,
        },
        title: { text: 'Monthly Spend', style: { fontSize: '14px' } },
        xAxis: {
            categories: trendMonths,
            labels: { rotation: -45 },
        },
        yAxis: {
            title: { text: 'Amount ($)' },
            gridLineWidth: 0.1,
        },
        // tooltip.backgroundColor, tooltip.style come from getChartTheme()
        series: [{ type: 'spline', name: 'Spend', data: trendAmounts, color: '#4A90D9' }],  // D-01: identity color fixed
        credits: { enabled: false },
    } as any));

    // ── Donut: category breakdown (UI-SPEC Component 4, mirrors stats() donut) ──
    // Compute total for center-floating subtitle via abbreviateCurrency
    const totalBreakdown = merchant_breakdown.reduce((s: number, d: any) => s + d.total, 0);

    Highcharts.chart('merchant-donut-chart', Highcharts.merge(getChartTheme(), {
        chart: {
            type: 'pie',
            height: 350,
        },
        title: { text: 'Spending by Category', style: { fontSize: '14px' } },
        subtitle: {
            text: abbreviateCurrency(totalBreakdown),
            floating: true,
            verticalAlign: 'middle',
            y: 10,
            style: { fontSize: '16px', fontWeight: 'bold' },
        },
        legend: {
            enabled: true,
            layout: 'vertical' as const,
            align: 'right' as const,
            verticalAlign: 'middle' as const,
        },
        plotOptions: {
            pie: {
                innerSize: '60%',
                showInLegend: true,
                dataLabels: { enabled: false },
            },
        },
        series: [{
            type: 'pie',
            name: 'Amount',
            data: merchant_breakdown.map((d: any) => {
                const p: any = { name: d.main_name, y: d.total };
                if (d.main_color) p.color = d.main_color;  // conditional — never explicit undefined (feedback_highcharts_undefined_options)
                return p;
            }),
        }],
        credits: { enabled: false },
    } as any));

    // ── Merchant-scoped server-side DataTable (UI-SPEC Component 6) ─────────
    // Sends merchantKey in POST body — handler reads getPost('merchantKey') (D-09)
    // No self-link on the Name column for the detail page (UI-SPEC §Component 6)
    new DataTable('.datatable[data-table="merchant_list"]', {
        pageLength: 20,
        processing: true,
        serverSide: true,
        ajax: {
            // GET (mirrors the proven main-list table). Everything rides in the query string:
            //  - name=  → satisfies actionMerchant($name), which Nette re-runs on every signal
            //    request to this presenter (a bare '?do=getTransactions' drops it → 404 "Missing
            //    parameter $name");
            //  - draw/start/length/search/order → fill handleGetTransactions() method params,
            //    which Nette fills from QUERY, not POST body (Phase 150/151 lesson);
            //  - merchantKey → read by the handler's getPost() ?? getQuery() fallback (D-09).
            // (Phase 156 UAT fix — POST left draw/start/length unfillable + dropped name.)
            url: '?do=getTransactions&name=' + encodeURIComponent(merchant_key),
            data: function(d: any) {
                d.merchantKey = merchant_key;  // D-09: scope to this merchant (query param under GET)
                return d;
            },
        },
        columns: [
            { data: 'date' },
            { data: 'name' },  // plain text on merchant page — no link back to itself (UI-SPEC §Component 6)
            { data: null, render: function(data: any) { return '$' + number_format(data.amount, 2); } },
            { data: null, render: function(data: any) {
                // Flow badge — D-08 visual distinction for credit/income rows
                if (data.flow === 'debit')   return '<span class="badge bg-danger bg-opacity-10 text-danger">Debit</span>';
                if (data.flow === 'credit')  return '<span class="badge bg-success bg-opacity-10 text-success">Credit</span>';
                if (data.flow === 'income')  return '<span class="badge bg-info bg-opacity-10 text-info">Income</span>';
                return data.flow ?? '';
            }},
            { data: null, render: function(data: any) { return data.group ?? ''; } },
            { data: null, render: function(data: any) { return data.description ?? ''; } },
        ],
        order: [[0, 'desc']] as Order[],
    });
}
